/**
 * Resolve which paragraph style new insertions should match when inserting after a
 * parent heading that already has sub-clause children (heading 2 / heading 3 …).
 */

export type InsertionStyleReference = {
  /** Word body paragraph index whose `style` should be copied. */
  refWordIdx: number | null;
  /** Used when no reference paragraph exists (e.g. parent with no children yet). */
  fallbackStyle: string | null;
};

/** Parse outline level from Word style name (English or Hebrew templates). */
export function parseHeadingLevelFromStyleName(style: string): number | null {
  const s = (style || '').trim();
  if (!s) return null;
  let m = /heading\s*(\d+)/i.exec(s);
  if (m) return parseInt(m[1], 10);
  m = /כותרת\s*(\d+)/.exec(s);
  if (m) return parseInt(m[1], 10);
  return null;
}

export function headingStyleNameForLevel(level: number): string {
  return `heading ${Math.max(1, Math.min(9, level))}`;
}

/**
 * When inserting after `anchorWordIdx`, choose a sibling/child paragraph style so new
 * content stays at the same hierarchy as existing sub-clauses (not as the next top-level item).
 */
export function resolveInsertionStyleReference(
  anchorWordIdx: number,
  itemsLength: number,
  wordText: (wordIdx: number) => string,
  stylesByWordIdx: Map<number, string>,
  scanAhead = 24,
): InsertionStyleReference {
  const anchorStyle = stylesByWordIdx.get(anchorWordIdx) ?? '';
  const anchorLevel = parseHeadingLevelFromStyleName(anchorStyle);
  if (anchorLevel == null) {
    return { refWordIdx: null, fallbackStyle: null };
  }

  if (anchorLevel >= 2) {
    return { refWordIdx: anchorWordIdx, fallbackStyle: anchorStyle || null };
  }

  const limit = Math.min(itemsLength, anchorWordIdx + scanAhead + 1);
  for (let wi = anchorWordIdx + 1; wi < limit; wi++) {
    if (!wordText(wi)) continue;
    const childLevel = parseHeadingLevelFromStyleName(stylesByWordIdx.get(wi) ?? '');
    if (childLevel != null && childLevel > anchorLevel) {
      return { refWordIdx: wi, fallbackStyle: stylesByWordIdx.get(wi) ?? null };
    }
    if (childLevel != null && childLevel <= anchorLevel) {
      break;
    }
  }

  return {
    refWordIdx: null,
    fallbackStyle: headingStyleNameForLevel(anchorLevel + 1),
  };
}
